import React, { useEffect,useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Message, QuickReply } from "../utils/types";
import CardCarousel from "./CardCarousel";
import SuggestionChips from "./suggestionChips";
import MultiSelectChips from "./MultiSelectChips";
import { formatTime } from "../App";
import ImageSlider from "./ImageSlider";
import ImageSliderDesktop from "../components/ImageSliderDesktop";


interface MessageRowProps {
  message: Message;
  index: number;
  userAvatar: string;
  botAvatar: string;
  sendMessage: (msg: string, backendPayload?: string, isFromChip?: boolean) => void;
  scrollCarousel: (index: number, dir: number) => void;
  carouselRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  setResponding: React.Dispatch<React.SetStateAction<boolean>>;
  isResponding: boolean; 
  messages: Message[];
  isLastMessage: boolean;
  isDarkMode:boolean;
  isBeforeChips?: boolean;
  setInput: (val: string) => void;
  selectedMultiChips?: string[];
  setSelectedMultiChips?: React.Dispatch<React.SetStateAction<string[]>>;
}


const MessageRow = ({
  message,
  index,
  userAvatar,
  botAvatar,
  sendMessage,
  scrollCarousel,
  setResponding,
  isResponding,
  carouselRefs,
  messages,
  isDarkMode,
  isLastMessage,
  isBeforeChips,
  setInput,
  selectedMultiChips,
  setSelectedMultiChips,
}: MessageRowProps) => {

    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 573);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 573);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  
  // Aggiungi questo log per debug
  useEffect(() => {
    console.log(`[MESSAGEROW] Renderizzo un messaggio (ID: ${message.id}). Contiene immagini?`, !!message.images);
    if (message.images) {
      console.log("[MESSAGEROW] Immagini ricevute:", message.images);
    }
  }, [message]);

  

  // Trova l'indice dell'ultimo messaggio che contiene cards
  const lastCardMessageIndex = messages.map((msg, idx) => ({ msg, idx }))
    .filter(({ msg }) => !!msg.cards)
    .pop()?.idx;

  // Questo messaggio ha i bottoni abilitati solo se è l'ultimo messaggio con cards e non si sta rispondendo
  const isLastCardMessage = !!message.cards && index === lastCardMessageIndex && !isResponding;

  // Per le singole card, abilita i bottoni solo se non si sta rispondendo
  const isSingleCardEnabled = !!message.card && !isResponding;

   return (

    <div className={`message-row ${message.isUser ? "user-row" : "bot-row"} ${isBeforeChips ? "before-chips" : ""}`}>
      {/* Show avatar if no suggestion chips and no multi select chips */}
      {!message.suggestionChips && !message.multiQuickReplies && (
        <img
          src={message.isUser ? userAvatar : botAvatar}
          alt={message.isUser ? "User" : "Bot"}
          className="avatar"
        />
      )}

      {message.isTyping ? (
        <div className="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      ) : message.text ? (
        <div className={`message ${message.isUser ? "user-message" : "bot-message"} ${!message.isUser ? "markdown-body" : ""}`}>

          {message.isUser ? (
            <div className="text-content">{message.text}</div>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {message.text}
            </ReactMarkdown>
          )}
          {message.timestamp && (
            <span className="timestamp">{formatTime(message.timestamp)}</span>
          )}
        </div>) : message.images ? (
          <div className="message bot-message">
          {isDesktop ? (
            <ImageSliderDesktop images={message.images} />
          ) : (
            <ImageSlider images={message.images} />
          )}
          {message.timestamp && (
            <span className="timestamp">{formatTime(message.timestamp)}</span>
          )}
        </div>
      ) : message.cards ? (
        <div className="message">
          <CardCarousel
            cards={message.cards}
            carouselRef={(el) => { 
                carouselRefs.current[index] = el; 
            }}
            scrollCarousel={(dir) => scrollCarousel(index, dir)}
            onButtonClick={(cardTitle, payload) => {
                const visualMessage = `⛵️ Informazioni sull'imbarcazione: ${cardTitle}`;
                console.log("Invio visualMessage e payload:", { visualMessage, payload });
                sendMessage(visualMessage, payload, true);
            }}
            isLastCardMessage={isLastCardMessage} 
          />
          {message.timestamp && (
            <span className="timestamp">{formatTime(message.timestamp)}</span>
          )}
        </div>
      ) : message.card ? (
        <div className="message">
          <CardCarousel
            cards={[message.card]}
            carouselRef={(el) => { 
                carouselRefs.current[index] = el; 
            }}
            scrollCarousel={(dir) => scrollCarousel(index, dir)}
            onButtonClick={(cardTitle, payload) => {
                const visualMessage = `⛵️ Informazioni sull'imbarcazione: ${cardTitle}`;
                console.log("Invio visualMessage e payload:", { visualMessage, payload });
                sendMessage(visualMessage, payload, true);
            }}
            isLastCardMessage={isSingleCardEnabled} 
          />
          {message.timestamp && (
            <span className="timestamp">{formatTime(message.timestamp)}</span>
          )}
        </div>
      ) : null}

      {/* Suggestion chips, if any */}
      {message.suggestionChips && (
        <>
          <SuggestionChips
            chips={message.suggestionChips}
            onChipClick={(chip: QuickReply) => {
              sendMessage(chip.title, chip.payload, true);
              setResponding(true);
            }}  
          />
        </>
      )}

      {/* Multi select chips, if any */}
      {message.multiQuickReplies && isLastMessage && (
        <MultiSelectChips
          chips={message.multiQuickReplies}
          selectedChips={selectedMultiChips || []}
          disabled={isResponding}
          onSelectionChange={(selected) => {
            if (setSelectedMultiChips) {
              setSelectedMultiChips(selected.map(c => c.title));
            }
          }}
        />
      )}
    </div>
  );
};

export default MessageRow;
